import { ComponentType, useCallback, useMemo } from 'react';

import { parseEntityRef, stringifyEntityRef } from '@backstage/catalog-model';
import {
  Content,
  ContentHeader,
  DocsIcon,
  Header,
  Page,
  SupportButton,
} from '@backstage/core-components';
import { useApp, useRouteRef } from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { catalogImportPlugin } from '@backstage/plugin-catalog-import';
import {
  CatalogFilterLayout,
  EntityKindPicker,
  EntityListProvider,
  EntityOwnerPicker,
  EntitySearchBar,
  EntityTagPicker,
  UserListPicker,
} from '@backstage/plugin-catalog-react';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import {
  TemplateListPageProps,
  scaffolderTranslationRef,
} from '@backstage/plugin-scaffolder/alpha';
import { TemplateEntityV1beta3 } from '@backstage/plugin-scaffolder-common';
import {
  ScaffolderPageContextMenu,
  TemplateCategoryPicker,
} from '@backstage/plugin-scaffolder-react/alpha';
import { techdocsPlugin } from '@backstage/plugin-techdocs';
import { useNavigate } from 'react-router-dom';

import { createTemplateGroupsWithOther } from '../../utils/createTemplateGroupsWithOther';
import { buildScaffolderPageContextMenuProps } from '../../utils/scaffolderPageContextMenu';
import { buildTechdocsTemplateLinks } from '../../utils/techdocsTemplateLinks';
import { AlphabeticalTemplateGroups } from '../AlphabeticalTemplateGroups/AlphabeticalTemplateGroups';
import { RegisterExistingButton } from '../RegisterExistingButton/RegisterExistingButton';

/**
 * Scaffolder template list page with templates sorted alphabetically by title.
 */
export const AlphabeticalTemplateListPage = (props: TemplateListPageProps) => {
  const registerComponentLink = useRouteRef(
    catalogImportPlugin.routes.importPage,
  );
  const {
    TemplateCardComponent,
    groups: givenGroups = [],
    templateFilter,
    headerOptions,
  } = props;
  const navigate = useNavigate();
  const editorLink = useRouteRef(scaffolderPlugin.routes.edit);
  const actionsLink = useRouteRef(scaffolderPlugin.routes.actions);
  const tasksLink = useRouteRef(scaffolderPlugin.routes.listTasks);
  const viewTechDocsLink = useRouteRef(techdocsPlugin.routes.docRoot);
  const templateRoute = useRouteRef(scaffolderPlugin.routes.selectedTemplate);
  const templatingExtensionsLink = useRouteRef(
    scaffolderPlugin.routes.templatingExtensions,
  );
  const app = useApp();
  const { t } = useTranslationRef(scaffolderTranslationRef);

  const groups = useMemo(
    () =>
      givenGroups.length
        ? createTemplateGroupsWithOther(
            givenGroups,
            t('templateListPage.templateGroups.otherTitle'),
          )
        : [
            {
              title: t('templateListPage.templateGroups.defaultTitle'),
              filter: () => true,
            },
          ],
    [givenGroups, t],
  );

  const scaffolderPageContextMenuProps = buildScaffolderPageContextMenuProps(
    props.contextMenu,
    navigate,
    {
      editor: editorLink,
      actions: actionsLink,
      tasks: tasksLink,
      templatingExtensions: templatingExtensionsLink,
    },
  );

  const additionalLinksForEntity = useCallback(
    (template: TemplateEntityV1beta3) =>
      buildTechdocsTemplateLinks(template, {
        viewTechDocsLink: viewTechDocsLink ?? undefined,
        docsIcon: app.getSystemIcon('docs') ?? DocsIcon,
        linkTitle: t(
          'templateListPage.additionalLinksForEntity.viewTechDocsTitle',
        ),
      }),
    [app, viewTechDocsLink, t],
  );

  const onTemplateSelected = useCallback(
    (template: TemplateEntityV1beta3) => {
      const { namespace, name } = parseEntityRef(stringifyEntityRef(template));

      navigate(templateRoute({ namespace, templateName: name }));
    },
    [navigate, templateRoute],
  );

  return (
    <EntityListProvider>
      <Page themeId="home">
        <Header
          pageTitleOverride={t('templateListPage.pageTitle')}
          title={t('templateListPage.title')}
          subtitle={t('templateListPage.subtitle')}
          {...headerOptions}
        >
          <ScaffolderPageContextMenu {...scaffolderPageContextMenuProps} />
        </Header>
        <Content>
          <ContentHeader>
            <RegisterExistingButton
              title={t(
                'templateListPage.contentHeader.registerExistingButtonTitle',
              )}
              to={registerComponentLink?.()}
            />
            <SupportButton>
              {t('templateListPage.contentHeader.supportButtonTitle')}
            </SupportButton>
          </ContentHeader>

          <CatalogFilterLayout>
            <CatalogFilterLayout.Filters>
              <EntitySearchBar />
              <EntityKindPicker initialFilter="template" hidden />
              <UserListPicker
                initialFilter="all"
                availableFilters={['all', 'starred']}
              />
              <TemplateCategoryPicker />
              <EntityTagPicker />
              <EntityOwnerPicker />
            </CatalogFilterLayout.Filters>
            <CatalogFilterLayout.Content>
              <AlphabeticalTemplateGroups
                groups={groups}
                templateFilter={templateFilter}
                TemplateCardComponent={
                  TemplateCardComponent as ComponentType<{
                    template: TemplateEntityV1beta3;
                  }>
                }
                onTemplateSelected={onTemplateSelected}
                additionalLinksForEntity={additionalLinksForEntity}
              />
            </CatalogFilterLayout.Content>
          </CatalogFilterLayout>
        </Content>
      </Page>
    </EntityListProvider>
  );
};
